import {
  activeThemeSeed,
  namedThemeSeed,
  SHIPPED_THEME_SEEDS,
  DEFAULT_THEME_APPEARANCE,
  useTheme,
  type ThemeColorMode,
  type ThemeColorSeed,
} from "@salestrak/ui";
import React from "react";

import { useThemeAppearance } from "#app/components/theme/theme-appearance-provider";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";

export type ThemeMode = "light" | "dark" | "system";

/**
 * The mode a theme control EDITS, which is not always the mode painting.
 *
 * The appearance screen shows a Light section and a Dark section side by side,
 * so a control mounted in the dark section must edit the dark half while the
 * light one is on screen. Travelling by context rather than by prop for the
 * same reason `surface` and `density` do: the roster of controls is open-ended
 * and every one of them would otherwise have to thread a mode it does not
 * otherwise care about.
 *
 * Unset — the theme popup, the account menu — means "the half that is
 * painting", which is what every existing call site already meant.
 */
const ThemeEditingModeContext = React.createContext<ThemeColorMode | null>(null);

export function ThemeEditingMode({
  mode,
  children,
}: {
  mode: ThemeColorMode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <ThemeEditingModeContext value={mode}>{children}</ThemeEditingModeContext>
  );
}

/** The half a control edits: the section it sits in, or the painting one. */
export function useThemeEditingMode(): ThemeColorMode {
  const override = React.useContext(ThemeEditingModeContext);
  const { resolvedTheme } = useTheme();
  return override ?? resolvedTheme;
}

export function useThemeModePreference(): {
  mode: string;
  setMode: (mode: ThemeMode) => void;
} {
  const { theme, setTheme } = useTheme();
  const { updatePreference } = useUserSettings();

  const setMode = React.useCallback(
    (next: ThemeMode) => {
      setTheme(next);
      void updatePreference("themeMode", next);
    },
    [setTheme, updatePreference],
  );

  return { mode: theme, setMode };
}

export function useEditedThemeColors(): {
  value: ThemeColorSeed;
  preview: (seed: ThemeColorSeed) => void;
  commit: (seed: ThemeColorSeed) => void;
} {
  const resolvedTheme = useThemeEditingMode();
  const { appearance, committed, preview, commit } = useThemeAppearance();
  // An untouched half shows the colours its THEME supplies — which is what the
  // rows are for: a theme drives Accent, Background and Foreground, and any of
  // them can be overwritten. Editing writes the seed and the half becomes the
  // user's from then on.
  const value = appearance.custom[resolvedTheme] ?? activeThemeSeed(
    appearance,
    resolvedTheme,
  );

  const write = (seed: ThemeColorSeed, persist: boolean) =>
    (persist ? commit : preview)({
      ...committed,
      custom: { ...committed.custom, [resolvedTheme]: seed },
    });

  return {
    value,
    preview: (seed) => write(seed, false),
    commit: (seed) => write(seed, true),
  };
}

// ---------------------------------------------------------------------------
// Two axes, because a theme is a PAIR.
//
// A catalog theme carries a light half and a dark half, so it cannot sit in one
// list beside "Light" and "Dark" without either flattening to two rows per
// theme or taking the mode choice away. So the palette and the mode are
// separate controls: THEME picks the pair, APPEARANCE picks which half paints.
//
// Custom is a theme in the palette list like any other. Its halves are authored
// on demand — editing in dark writes the dark half and leaves the light one
// alone — and an unauthored half falls through to the shipped design, which is
// the same thing a catalog theme shipping only one half does.
// ---------------------------------------------------------------------------

/** `default` is the shipped pair, `custom` the user's own; anything else is a
 *  catalog key. Not a union of keys: the catalog is data, and a stored key it
 *  no longer carries has to resolve to something rather than fail to typecheck. */
export const PALETTE_DEFAULT = "default";

/**
 * The palette axis. DERIVED, not stored: `customActive` supersedes the named
 * key, so picking Custom never discards the named choice and picking a named
 * theme never discards the user's seeds — the same retention rule the seeds
 * have always had.
 */
export function useThemePalette(): {
  value: string;
  setValue: (next: string) => void;
} {
  const { committed, commit } = useThemeAppearance();

  // Selection is per MODE, so this control reads and writes the half the
  // viewer is actually looking at and leaves the other one exactly as it is.
  const resolvedTheme = useThemeEditingMode();
  const value = committed.theme[resolvedTheme] ?? PALETTE_DEFAULT;

  // Picking a theme SEEDS the half and discards whatever was there — the same
  // thing the reference application does. Its colours become the row values,
  // and editing one from there makes the half the user's again. Switching away
  // and back therefore RESETS rather than restoring, which is the behaviour
  // read out of the reference's own settings hook.
  //
  // The workspace accent is part of "whatever was there": `activeThemeSeed`
  // substitutes a set accent over EVERY theme's own, so left behind it would
  // keep painting on top of each pick and no theme could ever land with the
  // accent it ships. A pick promises the theme's colours, accent included.
  const setValue = React.useCallback(
    (next: string) => {
      const key = next === PALETTE_DEFAULT ? null : next;
      commit({
        ...committed,
        accent: null,
        theme: { ...committed.theme, [resolvedTheme]: key },
        custom: { ...committed.custom, [resolvedTheme]: null },
      });
    },
    [commit, committed, resolvedTheme],
  );

  return { value, setValue };
}

/** The mode axis. Wraps the DOM theme so the two controls read alike. */
export function useThemeAppearanceMode(): {
  value: ThemeMode;
  setValue: (next: ThemeMode) => void;
} {
  const { mode, setMode } = useThemeModePreference();
  const value: ThemeMode =
    mode === "light" || mode === "dark" ? mode : "system";
  return { value, setValue: setMode };
}

/** The seed a palette paints in a mode, for a swatch or a specimen. Falls back
 *  to the shipped pair, which is what an absent half actually renders. */
export function paletteSeed(
  palette: string,
  mode: "light" | "dark",
): ThemeColorSeed {
  return namedThemeSeed(palette, mode) ?? SHIPPED_THEME_SEEDS[mode];
}


/**
 * One mode's whole section: the theme, its three colours, its contrast, and
 * the shape switches a palette may or may not offer.
 *
 * It owns the `SettingsGroup` rather than the page because the shape rows are
 * CONDITIONAL and the condition needs `useThemeShape()`, which only answers
 * inside `ThemeEditingMode`. A page rendering them would have to pass elements
 * that return null, and `SettingsGroup` draws a separator per direct child:
 * `React.Children.toArray` strips a null child but KEEPS an element that
 * merely renders null, so the last real row gets a rule under it doubled
 * against the card's own edge.
 */
/** Reads committed settings only; previews do not create a false reset state.
 *  Dormant seeds (retained while a stock theme is active) do NOT count —
 *  the interface is painting stock, so there is nothing visible to reset.
 *  The named ACCENT counts: no mounted control writes one any more (the Accent
 *  row writes the seed and clears it), so a stored value is legacy state that
 *  paints over every theme with nothing on screen naming it — this affordance
 *  is its one way out. */
export function useIsAppearanceCustomized(): boolean {
  const { committed } = useThemeAppearance();
  return (
    committed.accent !== null ||
    committed.theme.light !== null ||
    committed.theme.dark !== null ||
    committed.custom.light !== null ||
    committed.custom.dark !== null
  );
}

/** The one reset write, shared by the preferences button and the theme
 *  popup's menu row: the shipped design, whole — theme, seeds, and the
 *  workspace accent, which otherwise has no control left to clear it. */
export function useResetAppearance(): () => void {
  const { commit } = useThemeAppearance();
  return React.useCallback(
    () => commit(DEFAULT_THEME_APPEARANCE),
    [commit],
  );
}
